import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import {
  clientAddressesApi,
  type CreateClientAddressRequest,
} from "@/lib/clients-accounts";

export interface NewClientAddressDialogProps {
  clientId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new ClientAddress.id on successful POST. */
  onCreated: (newAddressId: number) => void;
}

export function NewClientAddressDialog({
  clientId,
  open,
  onOpenChange,
  onCreated,
}: NewClientAddressDialogProps) {
  const qc = useQueryClient();
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postCode, setPostCode] = useState("");

  const createMut = useMutation({
    mutationFn: (body: CreateClientAddressRequest) =>
      clientAddressesApi.create(clientId, body),
    onSuccess: (created) => {
      toast.success("Address added");
      void qc.invalidateQueries({ queryKey: ["client-addresses", clientId] });
      onCreated(created.id);
      // Reset fields & close
      setAddress("");
      setCity("");
      setState("");
      setPostCode("");
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(`Add failed: ${err.message}`),
  });

  const handleSave = () => {
    if (!address.trim()) {
      toast.error("Address line is required");
      return;
    }
    createMut.mutate({
      address: address.trim(),
      city: city.trim() || null,
      state: state.trim() || null,
      postCode: postCode.trim() || null,
      clientOwnsAddress: null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add address</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <DialogField
            id="new-addr-address"
            label="Address"
            value={address}
            onChange={setAddress}
          />
          <DialogField
            id="new-addr-city"
            label="City"
            value={city}
            onChange={setCity}
          />
          <DialogField
            id="new-addr-state"
            label="State"
            value={state}
            onChange={setState}
          />
          <DialogField
            id="new-addr-pc"
            label="Postcode"
            value={postCode}
            onChange={setPostCode}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setAddress("");
              setCity("");
              setState("");
              setPostCode("");
              onOpenChange(false);
            }}
            disabled={createMut.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={createMut.isPending}>
            {createMut.isPending ? "Saving…" : "Save Address"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
